import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { v4 as uuidv4 } from 'uuid'
import { FbStandardEvent } from '../../constants'
import type { FacebookPixel } from '../../types'
import type { EventProperties } from '../mixpanel/types'
import { fbStandardEvent } from './facebook-pixel.service'

// Maps Taka analytics events to Meta STANDARD events. These fire ALONGSIDE the
// existing custom events (see use-analytics.ts), so anything ad sets reference
// today keeps working. The point is to give Meta a real money signal to
// optimize toward instead of the custom "Onboarding Completed" proxy.

// Fresh eventID per fire. Lets Meta dedup an event that fires twice (e.g. a
// component remount), and lets a future CAPI send reuse the same ID for
// server/browser dedup.
const eventId = (): FacebookPixel.EventIDOptions => ({ eventID: uuidv4() })

// Tester grants and comped checkouts go through the same CHECKOUT_* events but
// charge nothing. They must never reach Meta as a purchase signal.
const isComped = (properties?: EventProperties): boolean =>
  properties?.skipped === true || properties?.reason === 'tester_grant'

// Checkout events also fire for credit top-ups, which carry no plan_name. Only
// the subscription shape should reach Meta, since the campaigns recruit
// subscribers, not top-up buyers.
const subscriptionValue = (
  properties?: EventProperties
): { value: number; currency: string } | null => {
  if (!properties || properties.plan_name == null) return null
  if (isComped(properties)) return null

  const value = typeof properties.price === 'number' ? properties.price : undefined
  const currency = typeof properties.currency === 'string' ? properties.currency : undefined
  if (value == null || !currency) return null

  return { value, currency }
}

type StandardEventEmitter = (properties?: EventProperties) => void

export const META_STANDARD_EVENT_MAP: Record<string, StandardEventEmitter> = {
  [AnalyticsEvents.Onboarding.ONBOARDING_COMPLETED]: () => {
    fbStandardEvent(FbStandardEvent.CompleteRegistration, undefined, eventId())
  },
  [AnalyticsEvents.Paywall.CHECKOUT_STARTED]: properties => {
    const params = subscriptionValue(properties)
    if (!params) return
    fbStandardEvent(FbStandardEvent.InitiateCheckout, params, eventId())
  },
  [AnalyticsEvents.Paywall.CHECKOUT_COMPLETED]: properties => {
    const params = subscriptionValue(properties)
    if (!params) return

    // Yearly plans complete checkout into a free trial: nothing is charged yet,
    // so report a trial start (worth the plan price if it converts), not a
    // purchase. Direct/monthly checkouts charge immediately, so report a single
    // Subscribe (Subscribe is the canonical subscription money event; firing
    // Purchase too would double-count value).
    if (properties?.includes_trial === true) {
      fbStandardEvent(FbStandardEvent.StartTrial, params, eventId())
      return
    }

    fbStandardEvent(FbStandardEvent.Subscribe, params, eventId())
  },
}
